import { Button, Flex, Heading, Input, Text, VStack } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { getThemeColor } from "theme/colors";

interface TwoFactorChallengeProps {
  /** Verify the code; should throw on an invalid/expired code or lockout. */
  onVerify: (code: string) => Promise<void>;
  /** Called after a successful verification. */
  onComplete: () => void;
  /** Optional "use a different account" / back action. */
  onCancel?: () => void;
}

/**
 * Code-entry step shown at login for users who already have an authenticator enrolled.
 */
export default function TwoFactorChallenge({
  onVerify,
  onComplete,
  onCancel,
}: TwoFactorChallengeProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const trimmed = code.trim();
    if (trimmed.length !== 6) {
      setError(t("app.twoFactor.invalidCodeFormat"));
      return;
    }
    setVerifying(true);
    try {
      await onVerify(trimmed);
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.message || t("app.twoFactor.verifyFailed"));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack gap={5} align="stretch">
        <VStack gap={1} align="start">
          <Heading size="md">{t("app.twoFactor.challenge.title")}</Heading>
          <Text fontSize="sm" color={getThemeColor("text.secondary")}>
            {t("app.twoFactor.challenge.instruction")}
          </Text>
        </VStack>

        <Input
          id="twoFactorCode"
          label={t("app.twoFactor.codeLabel")}
          placeholder="000000"
          size="lg"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          autoFocus
        />

        {error && (
          <Text
            fontSize="sm"
            color={getThemeColor("alert.error.text")}
            fontWeight="medium"
          >
            {error}
          </Text>
        )}

        <Button
          intent="primary"
          type="submit"
          disabled={verifying}
          size="lg"
          w="full"
        >
          {verifying
            ? t("app.twoFactor.verifying")
            : t("app.twoFactor.challenge.verify")}
        </Button>

        {onCancel && (
          <Flex justify="center">
            <Button intent="ghost" type="button" onClick={onCancel}>
              {t("app.twoFactor.challenge.backToLogin")}
            </Button>
          </Flex>
        )}
      </VStack>
    </form>
  );
}
